import React, { memo, useCallback, useMemo, useState } from 'react';
import { Checkbox, Input, Menu, Table } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ApprovalWorkflowItemDto, AWAssetDto, AWStageDto } from '@api/Api';
import PageSortPanel from '@components/PageSortPanel';
import { useDebounced } from '@helpers/useDebounced';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { toggleApprovalAssetModal } from '@redux/actions/modalAction';

import ApprovalBulkManagement from '@components/Approval/ApprovalBulkActions/ApprovalBulkManagement';
import Status from '@components/Approval/Status';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';
import { QUERY_APPROVAL_APPROVAL_WORKFLOW } from '@hooks/queries/approval';
import LottieComponent from '@components/Lottie';

import NameCell from './components/TableCells/NameCell';
import ActionCell from './components/TableCells/ActionCell';
import DecisionsCell from './components/TableCells/DecisionsCell';
import CommentsCell from './components/TableCells/CommentsCell';

import './CampaignApprovalContent.less';

type CampaignApprovalContentProps = {
  approvalWorkflow: ApprovalWorkflowItemDto;
  currentStage: AWStageDto | null;
  isPastDueDate: boolean;
};

export default memo(function CampaignApprovalContent({
  approvalWorkflow,
  isPastDueDate,
  currentStage
}: CampaignApprovalContentProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAssets, setSelectedAssets] = useState<AWAssetDto[]>([]);
  const sortByParam = searchParams.get('sortBy') || 'needReview';
  const statusesParam = searchParams.getAll('statuses');
  const searchParam = searchParams.get('search');
  const searchText = useDebounced(searchParam, 600);

  const approvalIsFinished = approvalWorkflow.isFinished;
  const approvalIsPaused = approvalWorkflow.isPaused;

  const dataSource = useMemo(() => {
    if (!approvalWorkflow.assets?.length) return [];
    let { assets } = approvalWorkflow;
    if (searchText) {
      assets = assets.filter((asset) =>
        asset.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
      );
    }
    if (statusesParam.length) {
      assets = assets.filter((asset) => {
        const status = asset?.statuses?.find(
          (item) => item.stageId === currentStage?.id
        )?.status;
        return (
          statusesParam.includes(status || '') ||
          (status === 'pending' && statusesParam.includes('in_progress'))
        );
      });
    }
    return sortedAWListAssetsToReviewInBatch(assets, sortByParam);
  }, [
    approvalWorkflow.assets,
    currentStage?.id,
    sortByParam,
    searchText,
    statusesParam
  ]);

  const currentMember = useMemo(() => {
    if (!currentStage?.members?.length) return null;
    return currentStage.members.find((member) => !!member.me);
  }, [currentStage?.members]);

  const onChangeSelectedAssets = useCallback(
    (asset: AWAssetDto) => {
      setSelectedAssets((prevSelectedAssets) => {
        const existAsset = prevSelectedAssets.find(
          (item) => item.id === asset.id
        );
        if (existAsset)
          return prevSelectedAssets.filter((item) => item.id !== asset.id);
        return [...prevSelectedAssets, asset];
      });
    },
    [setSelectedAssets]
  );

  const isAllSelectedAssets =
    selectedAssets.length === approvalWorkflow.assets?.length;

  const onOpenAsset = useCallback(
    (asset: AWAssetDto) => {
      const assetUrl = asset.hasAccess
        ? `/media-viewer/${asset.id}/${asset.versionId}`
        : `/approval?id=${approvalWorkflow.id}&assetId=${asset.id}`;
      navigate(assetUrl, {
        state: {
          returnTo: location.pathname + location.search
        }
      });
    },
    [approvalWorkflow.id, location.pathname, location.search, navigate]
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="campaign-approval-table_head-name">
            <Checkbox
              disabled={!approvalWorkflow.permissions.manage}
              checked={isAllSelectedAssets}
              onChange={() =>
                isAllSelectedAssets
                  ? setSelectedAssets([])
                  : setSelectedAssets(approvalWorkflow.assets || [])
              }
              onClick={(e) => e.stopPropagation()}
            />
            Media
          </div>
        ),
        dataIndex: 'name',
        key: 'name',
        width: '370',
        render: (_name: string, asset: AWAssetDto) => {
          const isSelected = selectedAssets.some(
            (item) => item.id === asset.id
          );
          return (
            <NameCell
              asset={asset}
              isDisabled={!approvalWorkflow.permissions.manage}
              isSelected={isSelected}
              onClick={
                asset.accessDisabled ? undefined : () => onOpenAsset(asset)
              }
              onSelect={onChangeSelectedAssets}
            />
          );
        }
      },
      {
        title: 'Status',
        dataIndex: 'statuses',
        key: 'statuses',
        width: '19%',
        render: (statuses: AWAssetDto['statuses']) => {
          const status = statuses?.find(
            (item) => item.stageId === currentStage?.id
          )?.status;
          return (
            <Status
              isCurrentStage={
                currentStage?.id === approvalWorkflow.currentStageId
              }
              status={status || currentStage?.status || approvalWorkflow.status}
              approvalWorkflowStatus={approvalWorkflow.status}
              approvalWorkflowIsPaused={approvalWorkflow.isPaused}
              deadline={currentStage?.deadline}
            />
          );
        }
      },
      {
        title: 'Reviewers',
        dataIndex: 'versionId',
        key: 'versionId',
        width: '25%',
        render: (versionId: string) => (
          <DecisionsCell
            assetVersionId={versionId}
            isPastDueDate={isPastDueDate}
            members={currentStage?.members || []}
          />
        )
      },
      {
        title: 'Comments',
        dataIndex: 'versionId',
        key: 'versionId',
        width: '15%',
        render: (versionId: string, asset: AWAssetDto) => {
          return (
            <CommentsCell
              commentsCount={asset.commentsCount}
              onClick={() => onOpenAsset(asset)}
            />
          );
        }
      },
      {
        title: '',
        dataIndex: 'versionId',
        key: 'versionId',
        width: 42,
        render: (versionId: string, asset: AWAssetDto) => {
          return <ActionCell approval={approvalWorkflow} assetId={asset.id} />;
        }
      }
    ],
    [
      approvalIsFinished,
      approvalIsPaused,
      isAllSelectedAssets,
      approvalWorkflow,
      currentStage,
      isPastDueDate,
      currentMember,
      selectedAssets,
      onOpenAsset,
      onChangeSelectedAssets
    ]
  );

  return (
    <div className="campaign-approval_content">
      <div className="campaign-approval_content-head">
        <StatusesFilter
          onChange={(statuses) => {
            searchParams.delete('statuses');
            if (statuses.length) {
              statuses.forEach((status) =>
                searchParams.append('statuses', status)
              );
            }
            setSearchParams(searchParams);
          }}
          selectedStatuses={statusesParam}
        />
        <PageSortPanel
          isVisibleSortStyle={false}
          classNamePanel="campaign-approval_content-sort"
          entity="approvals"
          sortMenuModal={
            <Menu
              selectedKeys={[sortByParam]}
              style={{
                minWidth: 260
              }}
              onClick={({ key }) => {
                searchParams.set('sortBy', key);
                setSearchParams(searchParams);
              }}
              items={[
                { label: 'Need to review', key: 'needReview' },
                { label: 'Already completed review', key: 'completedReview' }
              ]}
            />
          }
          searchInputComponent={
            <Input
              style={{ marginRight: 16, padding: '8px 12px' }}
              placeholder="Search"
              prefix={<SearchSvg />}
              size="large"
              defaultValue={searchParam || ''}
              onChange={(e) => {
                const _searchText = e.target.value.trim();
                if (_searchText) searchParams.set('search', _searchText);
                if (!_searchText) searchParams.delete('search');
                setSearchParams(searchParams);
              }}
            />
          }
        />
      </div>
      <Table
        className="campaign-approval_content-table"
        columns={columns}
        dataSource={[...dataSource]}
        onRow={(asset) => {
          return {
            onClick: () => {
              dispatch(
                toggleApprovalAssetModal({
                  visible: true,
                  assetId: asset.id,
                  versionId: asset.versionId,
                  approvalWorkflowId: approvalWorkflow.id,
                  callback: (aw) => {
                    if (!aw) {
                      navigate(
                        `/campaigns/single/${approvalWorkflow.campaignId}?tab=approvals`
                      );
                    }
                  }
                })
              );
            }
          };
        }}
        rowKey="id"
        tableLayout="fixed"
        pagination={false}
        locale={{
          emptyText: (
            <>
              {!dataSource.length && (
                <div className="empty_state empty_state--search">
                  <LottieComponent className="icon" view="noSearchingResult" />
                  <div
                    className="main-body-text main-body-text--bold"
                    style={{ marginBottom: 8 }}
                  >
                    No results found
                  </div>
                  <p className="copy main-body-text main-body-text--dark-gray">
                    It seems we can’t find any results based on your search
                  </p>
                </div>
              )}
            </>
          )
        }}
      />
      <ApprovalBulkManagement
        approvalWorkflow={approvalWorkflow}
        assets={selectedAssets}
        onClose={() => setSelectedAssets([])}
        queryKey={[QUERY_APPROVAL_APPROVAL_WORKFLOW]}
      />
    </div>
  );
});
